// src/modals/LivretOverrideSection.jsx — DÉBLOCAGE MANUEL DU LIVRET (admin).
//
// OÙ : fiche réservation (ResaV2Modal) → onglet « Documents », juste sous
// « Liens voyageur ». C'est l'écran où le propriétaire traite un voyageur au cas
// par cas : il y voit le lien du livret qu'il s'apprête à envoyer, et juste en
// dessous l'état des fiches d'arrivée (« formulaire non rempli ») — c'est-à-dire
// le problème ET son remède au même endroit, jamais dans un réglage global.
//
// CE QUE FAIT LE BOUTON. Il pose la dérogation manuelle (migration
// 20261111000001, action serveur `livret_override_set`) : le livret s'ouvre pour
// CE voyageur quelles que soient les conditions de sa plateforme.
//
// 🔴 IL OUVRE AUSSI LE CODE DE LA PORTE. La dérogation lève les DEUX étages du
// verrou : le livret ET le module « instructions d'arrivée », qui porte les
// codes d'entrée. L'écran le dit AVANT le geste, en toutes lettres — ce n'est
// pas un détail d'implémentation, c'est ce que le propriétaire donne.
//
// 🔴 MOTIF OBLIGATOIRE. Le bouton de confirmation reste DÉSACTIVÉ tant que le
// motif est vide (le serveur refuse en 400 `motif_requis` — double barrière).
// La colonne ne peut pas être NOT NULL en base (deploy avant apply) : c'est donc
// ici que l'obligation vit. Demande explicite du propriétaire : « garde la trace
// (qui a débloqué, quand, pourquoi) — c'est ça qui me protège ».
//
// SANS LA MIGRATION. Elle n'est pas appliquée aujourd'hui : le serveur répond
// `available:false` et l'écran affiche « Fonction indisponible » à la place du
// bouton. Aucun plantage, aucun bouton qui échouerait.
//
// RGPD : ne jamais saisir de nom / email / téléphone voyageur dans le motif —
// l'aide sous le champ le rappelle.

import { useCallback, useEffect, useState } from "react"
import { Unlock, Lock, AlertTriangle, History } from "lucide-react"
import { supabase } from "../supabase"
import { DS } from "../lib/ui"
import { formatDateTimeFR } from "../lib/format"

// Texte d'avertissement AFFICHÉ AU MOMENT DU GESTE. Source unique (repris dans
// le bandeau d'état une fois la dérogation active) — ne pas le diluer.
export const AVERTISSEMENT_CODE_ACCES =
  "Ce déblocage ouvre le livret entier ET le module « Instructions d'arrivée » : " +
  "le CODE D'ACCÈS AU LOGEMENT sera affiché à ce voyageur, même s'il n'a pas rempli " +
  "le formulaire ni déposé la caution. Seule la preuve d'identité (4 derniers chiffres " +
  "de son téléphone) reste demandée."

async function callOverrideApi(action, body) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error("not_authenticated")
  const res = await fetch(`/api/beds24-mapping?action=${action}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  })
  let payload = null
  try { payload = await res.json() } catch (_) { /* réponse non-JSON */ }
  if (!res.ok || !payload || payload.ok !== true) {
    const err = new Error((payload && payload.error) || `http_${res.status}`)
    err.code = (payload && payload.error) || `http_${res.status}`
    err.detail = (payload && payload.message) || null
    throw err
  }
  return payload
}

// Messages d'erreur en clair (jamais un code brut à l'écran).
const ERREURS = {
  motif_requis: "Motif obligatoire : le déblocage n'a pas été posé.",
  motif_trop_long: "Motif trop long (500 caractères maximum).",
  migration_absente:
    "Fonction indisponible : la migration des colonnes de dérogation n'est pas encore appliquée en base.",
  reservation_introuvable: "Réservation introuvable côté serveur.",
  reservation_id_invalide: "Identifiant de réservation invalide.",
  not_authenticated: "Session expirée — reconnectez-vous.",
}
const messageErreur = (e) =>
  (e && ERREURS[e.code]) || (e && e.detail) || "Le déblocage n'a pas pu être enregistré."

const CARD = {
  border: "1px solid #e6ecf4",
  borderRadius: 8,
  padding: "10px 12px",
  background: "#f7f9fc",
}

export default function LivretOverrideSection({ item }) {
  const itemId = item && item.id ? item.id : null

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [state, setState] = useState(null) // { available, override, history }
  // 'idle' | 'confirm-pose' | 'confirm-retrait'
  const [mode, setMode] = useState("idle")
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [traceWarning, setTraceWarning] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  const load = useCallback(async () => {
    if (!itemId) { setState(null); setLoading(false); return }
    setLoading(true)
    setLoadError(null)
    try {
      const payload = await callOverrideApi("livret_override_get", {
        reservation_id: itemId,
      })
      setState({
        available: payload.available === true,
        override: payload.override || { active: false },
        history: Array.isArray(payload.history) ? payload.history : [],
      })
    } catch (e) {
      // Jamais de catch muet, jamais d'écran blanc : on affiche l'erreur.
      console.error("[LivretOverrideSection] get:", e && e.message ? e.message : e)
      setLoadError(messageErreur(e))
      setState(null)
    } finally {
      setLoading(false)
    }
  }, [itemId])

  useEffect(() => { load() }, [load])

  // Motif obligatoire — LA garde de l'écran. Reproduite côté serveur.
  const motifValide = reason.trim().length >= 3
  const active = !!(state && state.override && state.override.active)

  const submit = async (nextActive) => {
    setSubmitting(true)
    setActionError(null)
    setTraceWarning(false)
    try {
      const payload = await callOverrideApi("livret_override_set", {
        reservation_id: itemId,
        active: nextActive,
        ...(nextActive ? { reason: reason.trim() } : {}),
      })
      if (payload.trace_ok === false) setTraceWarning(true)
      setMode("idle")
      setReason("")
      await load()
    } catch (e) {
      console.error("[LivretOverrideSection] set:", e && e.message ? e.message : e)
      setActionError(messageErreur(e))
    } finally {
      setSubmitting(false)
    }
  }

  if (!itemId) return null

  return (
    <div data-testid="livret-override-section" style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, fontSize: 14, marginBottom: 8 }}>
        {active ? <Unlock size={16} strokeWidth={2} /> : <Lock size={16} strokeWidth={2} />}
        Déblocage manuel du livret
      </div>

      {loading && (
        <div style={{ fontSize: 12, color: DS.textMuted }}>Chargement de l'état du déblocage…</div>
      )}

      {!loading && loadError && (
        <div data-testid="livret-override-load-error" style={{ ...CARD, borderColor: "#f3c9c9", background: "#fdf3f3", fontSize: 12, color: DS.red }}>
          {loadError}{" "}
          <button type="button" onClick={load} style={{ background: "none", border: "none", color: DS.navy, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0 }}>
            Réessayer
          </button>
        </div>
      )}

      {/* Migration non appliquée → on le dit, on ne propose pas un bouton mort. */}
      {!loading && !loadError && state && !state.available && (
        <div data-testid="livret-override-unavailable" style={{ ...CARD, fontSize: 12, color: DS.textMuted }}>
          <strong style={{ color: DS.marineDark }}>Fonction indisponible.</strong>{" "}
          Les colonnes de dérogation ne sont pas encore présentes en base (migration
          <code style={{ fontSize: 11 }}> 20261111000001 </code>
          non appliquée). Le livret suit donc les conditions normales de la plateforme.
        </div>
      )}

      {!loading && !loadError && state && state.available && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

          {/* ── État courant ────────────────────────────────────────────── */}
          {active ? (
            <div data-testid="livret-override-actif" style={{ ...CARD, borderColor: "#f3d9a8", background: "#fdf7ec" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#8a5a00", marginBottom: 4 }}>
                🔓 Livret débloqué manuellement — code d'accès inclus
              </div>
              <div style={{ fontSize: 11, color: DS.marineDark, lineHeight: 1.5 }}>
                <div><strong>Par :</strong> {state.override.by || "— (non renseigné)"}</div>
                <div><strong>Le :</strong> {state.override.at ? formatDateTimeFR(state.override.at) : "—"}</div>
                <div><strong>Motif :</strong> {state.override.reason || "— (non renseigné)"}</div>
              </div>
              <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 6 }}>
                {AVERTISSEMENT_CODE_ACCES}
              </div>
            </div>
          ) : (
            <div data-testid="livret-override-inactif" style={{ ...CARD, fontSize: 12, color: DS.textMuted }}>
              Aucun déblocage manuel. Le livret de ce voyageur suit les conditions
              de sa plateforme (formulaire, caution) et le calendrier du séjour.
            </div>
          )}

          {/* ── Geste : poser ───────────────────────────────────────────── */}
          {!active && mode !== "confirm-pose" && (
            <div>
              <button
                type="button"
                data-testid="livret-override-open-pose"
                onClick={() => { setMode("confirm-pose"); setActionError(null) }}
                style={{ background: DS.navy, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              >
                Débloquer le livret manuellement…
              </button>
            </div>
          )}

          {!active && mode === "confirm-pose" && (
            <div data-testid="livret-override-confirm-pose" style={{ ...CARD, borderColor: "#f3d9a8", background: "#fffdf7" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10 }}>
                <AlertTriangle size={16} strokeWidth={2} color={DS.amber} style={{ flexShrink: 0, marginTop: 1 }} />
                <div data-testid="livret-override-avertissement" style={{ fontSize: 11, lineHeight: 1.55, color: DS.marineDark }}>
                  {AVERTISSEMENT_CODE_ACCES}
                </div>
              </div>

              <label htmlFor="livret-override-motif" style={{ display: "block", fontSize: 11, fontWeight: 700, color: DS.marineDark, marginBottom: 4 }}>
                Motif du déblocage <span style={{ color: DS.red }}>(obligatoire)</span>
              </label>
              <textarea
                id="livret-override-motif"
                data-testid="livret-override-motif"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Ex. : arrivée dimanche 22h, formulaire non rempli, voyageur joint par téléphone."
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d8e2ee", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontFamily: DS.fontBody, resize: "vertical" }}
              />
              <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 4 }}>
                Cette trace (qui, quand, pourquoi) est conservée : c'est elle qui vous
                protège en cas de litige. N'y écrivez ni nom, ni e-mail, ni téléphone
                du voyageur.
              </div>

              {!motifValide && (
                <div data-testid="livret-override-motif-requis" style={{ fontSize: 11, color: DS.red, marginTop: 6 }}>
                  Saisissez un motif pour activer le déblocage.
                </div>
              )}

              {actionError && (
                <div data-testid="livret-override-action-error" style={{ fontSize: 11, color: DS.red, marginTop: 6 }}>
                  {actionError}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button
                  type="button"
                  data-testid="livret-override-confirm"
                  disabled={!motifValide || submitting}
                  onClick={() => submit(true)}
                  style={{
                    background: !motifValide || submitting ? "#c9d3e0" : DS.terra,
                    color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px",
                    fontSize: 12, fontWeight: 700,
                    cursor: !motifValide || submitting ? "not-allowed" : "pointer",
                  }}
                >
                  {submitting ? "Déblocage…" : "Confirmer : ouvrir le livret et le code d'accès"}
                </button>
                <button
                  type="button"
                  data-testid="livret-override-cancel"
                  disabled={submitting}
                  onClick={() => { setMode("idle"); setReason(""); setActionError(null) }}
                  style={{ background: "none", border: "1px solid #d8e2ee", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, color: DS.marineDark, cursor: "pointer" }}
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* ── Geste : retirer ─────────────────────────────────────────── */}
          {active && mode !== "confirm-retrait" && (
            <div>
              <button
                type="button"
                data-testid="livret-override-open-retrait"
                onClick={() => { setMode("confirm-retrait"); setActionError(null) }}
                style={{ background: "none", border: `1px solid ${DS.navy}`, color: DS.navy, borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              >
                Retirer le déblocage…
              </button>
            </div>
          )}

          {active && mode === "confirm-retrait" && (
            <div data-testid="livret-override-confirm-retrait" style={{ ...CARD }}>
              <div style={{ fontSize: 11, lineHeight: 1.55, color: DS.marineDark, marginBottom: 8 }}>
                Le livret et les instructions d'arrivée <strong>se refermeront</strong> pour ce
                voyageur : il devra de nouveau remplir les conditions de sa plateforme.
                Le retrait est enregistré dans l'historique (qui, quand).
              </div>
              {actionError && (
                <div data-testid="livret-override-action-error" style={{ fontSize: 11, color: DS.red, marginBottom: 6 }}>
                  {actionError}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  data-testid="livret-override-confirm-retrait-btn"
                  disabled={submitting}
                  onClick={() => submit(false)}
                  style={{ background: DS.navy, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer" }}
                >
                  {submitting ? "Retrait…" : "Confirmer le retrait"}
                </button>
                <button
                  type="button"
                  data-testid="livret-override-cancel-retrait"
                  disabled={submitting}
                  onClick={() => { setMode("idle"); setActionError(null) }}
                  style={{ background: "none", border: "1px solid #d8e2ee", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, color: DS.marineDark, cursor: "pointer" }}
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* L'écriture a eu lieu mais l'historique n'a pas pu être écrit — on
              le dit plutôt que de laisser croire à une trace complète. */}
          {traceWarning && (
            <div data-testid="livret-override-trace-warning" style={{ fontSize: 11, color: DS.amber }}>
              Geste enregistré, mais l'historique n'a pas pu être écrit. Notez-le
              de votre côté et signalez-le.
            </div>
          )}

          {/* ── Historique (pose ET retrait) ─────────────────────────────── */}
          {state.history.length > 0 && (
            <div>
              <button
                type="button"
                data-testid="livret-override-history-toggle"
                onClick={() => setShowHistory((v) => !v)}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: DS.navy, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: 0 }}
              >
                <History size={13} strokeWidth={2} />
                {showHistory ? "Masquer l'historique" : `Historique des déblocages (${state.history.length})`}
              </button>
              {showHistory && (
                <div data-testid="livret-override-history" style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                  {state.history.map((h) => (
                    <div key={h.id} style={{ fontSize: 11, color: DS.marineDark, background: "#f7f9fc", border: "1px solid #e6ecf4", borderRadius: 6, padding: "5px 8px" }}>
                      <strong>{h.event === "clear" ? "Retrait" : "Déblocage"}</strong>
                      {" — "}{h.at ? formatDateTimeFR(h.at) : "date inconnue"}
                      {h.actor ? ` — ${h.actor}` : ""}
                      {h.event !== "clear" && h.reason ? (
                        <div style={{ color: DS.textMuted }}>Motif : {h.reason}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
